import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import HomeRepairServiceOutlinedIcon from '@mui/icons-material/HomeRepairServiceOutlined';
import HomeWorkOutlinedIcon from '@mui/icons-material/HomeWorkOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import { Popover, Tooltip, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import clsx from 'clsx';
import { FC, useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Roles, routes } from 'src/enums';
import { getImageURL, isOwnerOrEditor } from 'src/helpers';
import { setStartRoutePath, TAppSlice } from 'src/redux/appSlice';
import { useGetClientInfoQuery } from 'src/redux/services/clients';
import {
  useCreateOrganizationMutation,
  useGetOrgLkSelfConnectEnabledQuery,
} from 'src/redux/services/organization';
import { RootState } from 'src/redux/store';
import { TUserSlice } from 'src/redux/userSlice';
import { logout } from 'src/requests/oidc';
import { CustomButton } from './custom/CustomButton';
import styles from './TopBar.module.css';

const AccountPopover: FC<{
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  userImageUrl: string | undefined;
  profile: TUserSlice['profile'];
  menu: { path: string; title: string; icon: JSX.Element }[];
  roleInApp: TUserSlice['roleInApp'];
}> = ({ open, anchorEl, onClose, userImageUrl, profile, menu, roleInApp }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [createOrg, result] = useCreateOrganizationMutation();
  const { data: ConnectSSOEnabled } = useGetOrgLkSelfConnectEnabledQuery();

  useEffect(() => {
    if (roleInApp) {
      const isAdmin = [Roles.ADMIN, Roles.EDITOR, Roles.OWNER].includes(roleInApp);
      if (result.isSuccess && isAdmin) {
        handleNavigate(routes.customer);
      }
    }
  }, [result.isSuccess, roleInApp]);

  const handleNavigate = (path: string) => {
    if (routes.profile !== path) dispatch(setStartRoutePath(path));
    navigate(path);
    onClose();
  };

  const handleClickConnectSSO = () => {
    createOrg({}).unwrap();
  };

  return (
    <Popover
      id="accounts-popover"
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      classes={{ paper: styles.popover }}
    >
      <Button startIcon={<CloseOutlinedIcon />} onClick={onClose} className={styles.closeButton} />
      <div className={styles.account}>
        <Box>
          {userImageUrl ? (
            <img src={userImageUrl} className={styles.accountImage} />
          ) : (
            <PersonOutlineOutlinedIcon
              className={clsx(styles.accountImage, styles.accountImageDefault)}
            />
          )}
        </Box>
        <p className={clsx('text-15-medium', styles.accountText)}>
          {profile?.given_name
            ? `${profile?.given_name} ${profile?.family_name}`
            : profile?.nickname}
        </p>
        {profile?.email && <p className={clsx('text-14', styles.accountText)}>{profile?.email}</p>}
      </div>
      <div className={styles.menu}>
        {menu.length > 0
          ? menu.map((item) => (
              <Button
                onClick={() => handleNavigate(item.path)}
                key={item.title}
                className={styles.menuButton}
              >
                <div className={styles.menuIcon}>{item.icon}</div>
                <Typography className="text-14">{item.title}</Typography>
              </Button>
            ))
          : ConnectSSOEnabled && (
              <Button
                onClick={handleClickConnectSSO}
                className={clsx(styles.menuButton, styles.menuButtonAccent)}
              >
                <div className={styles.menuIcon}>
                  {<AddOutlinedIcon className={styles.addIcon} />}
                </div>
                <Typography className="text-14">Подключить sso</Typography>
              </Button>
            )}
      </div>
      <div className={styles.buttons}>
        <CustomButton
          startIcon={<PersonOutlineOutlinedIcon />}
          className={styles.exitButton}
          variant="custom"
          onClick={() => handleNavigate(routes.profile)}
        >
          Профиль
        </CustomButton>
        <CustomButton
          startIcon={<LogoutOutlinedIcon />}
          className={styles.exitButton}
          onClick={logout}
          variant="custom"
          color="secondary"
        >
          Выход
        </CustomButton>
      </div>
    </Popover>
  );
};

const mapStateToProps = (state: RootState) => ({
  profile: state.user.profile,
  roleInApp: state.user.roleInApp,
  roles: state.user.roles,
  clientProfile: state.app.clientProfile,
  startRoutePath: state.app.startRoutePath,
  orgId: state.user.orgId,
});

interface ITopBarProps {
  profile: TUserSlice['profile'];
  roleInApp: TUserSlice['roleInApp'];
  roles: TUserSlice['roles'];
  clientProfile: TAppSlice['clientProfile'];
  startRoutePath: TAppSlice['startRoutePath'];
  orgId: TUserSlice['orgId'];
}

const TopBarComponent: FC<ITopBarProps> = ({ profile, roleInApp, roles, clientProfile, orgId }) => {
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const [menu, setMenu] = useState<{ path: string; title: string; icon: JSX.Element }[]>([]);
  const userImageUrl = getImageURL(profile?.picture);
  const { data: orgClient } = useGetClientInfoQuery({ id: orgId || '' }, { skip: !orgId });

  const pathSegments = pathname.split('/');
  if (pathSegments[1]) {
    dispatch(setStartRoutePath(pathSegments[1]));
  }

  useEffect(() => {
    const navigation = [];
    if (roleInApp) {
      const isAdmin = [Roles.ADMIN, Roles.EDITOR, Roles.OWNER].includes(roleInApp);
      if (isAdmin) {
        const currentPath = roleInApp === Roles.ADMIN ? routes.customer : routes.system;

        navigation.push({
          path: currentPath,
          title: `ЛК ${orgClient?.name || clientProfile?.name}`,
          icon: <HomeWorkOutlinedIcon />,
        });
      }
    }

    if (roles && !isOwnerOrEditor(roleInApp)) {
      roles.forEach((roleWithClient) => {
        if (roleWithClient.role === Roles.EDITOR && roleWithClient.client.parent_id !== null) {
          navigation.push({
            path: routes.admin,
            title: `Малый кабинет`,
            icon: <HomeRepairServiceOutlinedIcon />,
          });
        }
      });
    }

    setMenu(navigation);
  }, [roleInApp, orgClient, roles]);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Box id="top-bar" className={styles.topBar}>
        <img className={styles.topBarLogo} src={getImageURL(clientProfile?.avatar) || ''} />
        <span className={styles.topBarTitle}>{clientProfile?.name}</span>
        <div className={styles.profile}>
          <Tooltip title={'Аккаунт ' + clientProfile?.name} arrow>
            <div onClick={handleClick}>
              {userImageUrl ? (
                <img src={userImageUrl} className={styles.profileImage} />
              ) : (
                <PersonOutlineOutlinedIcon
                  className={clsx(styles.profileImage, styles.profileImageDefault)}
                />
              )}
            </div>
          </Tooltip>
        </div>
      </Box>

      <AccountPopover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        userImageUrl={userImageUrl}
        profile={profile}
        menu={menu}
        roleInApp={roleInApp}
      />
    </>
  );
};

export const TopBar = connect(mapStateToProps)(TopBarComponent);
